// overview-parser.service.ts or inside your component
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class OverviewParserService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  parseOverviewWithIntro(htmlString: string): {
    intro: string;
    questions: { question: string; answer: string }[];
  } {
    // Check if we're in a browser environment
    if (!isPlatformBrowser(this.platformId)) {
      // Server-side fallback: return safe structure
      return this.parseOverviewWithIntroServer(htmlString);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const body = doc.body;
    const children = Array.from(body.children);

    const questions: { question: string; answer: string }[] = [];
    let intro = '';
    let currentIndex = 0;
    let foundFirstQuestion = false;

    const isQuestion = (el: Element): boolean => {
      const text = el.textContent?.trim() || '';
      return text.endsWith('?');
    };

    while (currentIndex < children.length) {
      const el = children[currentIndex];

      if (!foundFirstQuestion && isQuestion(el)) {
        foundFirstQuestion = true;
      }

      if (!foundFirstQuestion) {
        intro += el.outerHTML;
        currentIndex++;
        continue;
      }

      // Now parsing question-answer sections
      if (isQuestion(el)) {
        const question = el.outerHTML;
        let answer = '';
        currentIndex++;

        // Collect answer elements and text nodes until next question or end
        while (
          currentIndex < children.length &&
          !isQuestion(children[currentIndex])
        ) {
          const node = children[currentIndex];
          if (node.nodeType === Node.ELEMENT_NODE) {
            answer += node.outerHTML;
          } else if (node.nodeType === Node.TEXT_NODE) {
            answer += node.textContent;
          }
          currentIndex++;
        }

        // Also check for text nodes immediately after the question element
        let nextSibling = el.nextSibling;
        while (
          nextSibling &&
          nextSibling.nodeType === Node.TEXT_NODE &&
          nextSibling.textContent?.trim()
        ) {
          answer += nextSibling.textContent;
          nextSibling = nextSibling.nextSibling;
        }

        let trimmedAnswer = answer.trim();
        // If answer does not contain any HTML tags, wrap with <p>
        if (!/<[a-z][\s\S]*>/i.test(trimmedAnswer)) {
          trimmedAnswer = `<p>${trimmedAnswer}</p>`;
        }

        questions.push({ question, answer: trimmedAnswer });
      } else {
        currentIndex++;
      }
    }

    return { intro, questions };
  }

  /**
   * Server-side fallback parser using regex
   * This is a simplified parser that works without DOMParser
   */
  private parseOverviewWithIntroServer(htmlString: string): {
    intro: string;
    questions: { question: string; answer: string }[];
  } {
    const questions: { question: string; answer: string }[] = [];
    let intro = '';
    
    // Simple regex-based parsing for server-side
    // Split by HTML tags that might contain questions (ending with ?)
    const questionRegex = /<[^>]+>([^<]*\?[^<]*)<\/[^>]+>/gi;
    const matches = Array.from(htmlString.matchAll(questionRegex));
    
    if (matches.length === 0) {
      // No questions found, return everything as intro
      return { intro: htmlString, questions: [] };
    }

    // Find the position of the first question
    const firstQuestionMatch = matches[0];
    const firstQuestionIndex = htmlString.indexOf(firstQuestionMatch[0]);
    
    // Everything before the first question is intro
    intro = htmlString.substring(0, firstQuestionIndex).trim();
    
    // Parse questions and answers
    for (let i = 0; i < matches.length; i++) {
      const questionMatch = matches[i];
      const questionHtml = questionMatch[0];
      const questionText = questionMatch[1];
      
      // Find the start of the next question or end of string
      const questionStartIndex = htmlString.indexOf(questionHtml);
      const nextQuestionIndex = i < matches.length - 1 
        ? htmlString.indexOf(matches[i + 1][0])
        : htmlString.length;
      
      // Extract answer between current question and next question
      let answer = htmlString.substring(
        questionStartIndex + questionHtml.length,
        nextQuestionIndex
      ).trim();
      
      // Clean up answer
      if (!answer) {
        continue;
      }
      
      // If answer does not contain any HTML tags, wrap with <p>
      if (!/<[a-z][\s\S]*>/i.test(answer)) {
        answer = `<p>${answer}</p>`;
      }
      
      questions.push({ question: questionHtml, answer });
    }

    return { intro, questions };
  }
}
